/**
 * INTUITUS IDENTITY RESOLUTION ENGINE v2.1
 * Implements Conflict Detection (7.1) and Reconciliation Logic
 */
export const detectIdentityConflicts = (analyticsData, customUsers) => {
  const conflicts = [];
  const sessions = analyticsData.pageViews || [];
  const events = analyticsData.events || [];

  // 7.1.1 Email Mismatch Detection
  events.filter(e => e.eventName === 'form_submit').forEach(event => {
    const user = customUsers.find(u => u.linked_visitor_ids?.includes(event.visitorId));
    const submittedEmail = event.properties?.email;
    if (user && submittedEmail && user.source_email && submittedEmail !== user.source_email) {
      conflicts.push({
        type: 'email_mismatch',
        severity: 'high',
        description: `Form email (${submittedEmail}) mismatch with profile (${user.source_email})`,
        affected_ids: [user.user_serial],
        metadata: { visitorId: event.visitorId, submittedEmail }
      });
    }
  });

  // 7.1.2 Token Identity Mismatch
  events.filter(e => e.properties?.secure_token).forEach(event => {
    const tokenIdentity = event.properties.token_user_id;
    const sessionIdentity = event.userSerial;
    if (tokenIdentity && sessionIdentity && tokenIdentity !== sessionIdentity) {
      conflicts.push({
        type: 'token_mismatch',
        severity: 'high',
        description: `Encrypted token identity (${tokenIdentity}) does not match session credentials (${sessionIdentity})`,
        affected_ids: [sessionIdentity, tokenIdentity],
        metadata: { eventId: event.id }
      });
    }
  });

  // 7.1.3 Multi-account on Single Device
  const deviceMap = {};
  sessions.forEach(pv => {
    if (pv.userSerial) {
      if (!deviceMap[pv.visitorId]) deviceMap[pv.visitorId] = new Set();
      deviceMap[pv.visitorId].add(pv.userSerial);
    }
  });

  Object.entries(deviceMap).forEach(([visitorId, users]) => {
    if (users.size > 1) {
      conflicts.push({
        type: 'multi_account',
        severity: 'medium',
        description: `Shared Device: ${users.size} identities detected on node ${visitorId.substring(0,8)}`,
        affected_ids: Array.from(users),
        metadata: { visitorId }
      });
    }
  });

  return conflicts;
};

/**
 * Bulk Reconciliation Logic
 * 1. Skips identical records (8.3)
 * 2. Updates changed records (8.2)
 * 3. Creates new records (8.1)
 */
export const reconcileUsers = (existingUsers, importedUsers) => {
  const report = { created: 0, reconciled: 0, identical: 0 };
  const toUpsert = [];

  importedUsers.forEach(imported => {
    const existing = existingUsers.find(u => 
      u.user_serial === imported.sourceUserID || 
      u.source_user_id === imported.sourceUserID
    );

    if (!existing) {
      toUpsert.push({
        user_serial: imported.sourceUserID,
        source_user_id: imported.sourceUserID,
        source_member_id: imported.sourceMemberID,
        source_membership_id: imported.sourceMembershipID,
        source_alt_id_1: imported.sourceAltId1,
        source_alt_id_2: imported.sourceAltId2,
        source_alt_id_3: imported.sourceAltId3,
        source_email: imported.sourceEmail,
        source_alt_email_1: imported.sourceAltEmail1,
        first_name: imported.firstName || '',
        last_name: imported.lastName || '',
        postal_code: imported.postalCode || '',
        state: imported.state || '',
        membership_type: imported.membershipType || 'Free',
        membership_status: imported.membershipStatus || 'Active',
        user_group: imported.userGroup || 'customers'
      });
      report.created++;
    } else {
      // Logic for identical data check
      const isIdentical = 
        existing.source_member_id === imported.sourceMemberID &&
        existing.source_membership_id === imported.sourceMembershipID &&
        existing.source_email === imported.sourceEmail &&
        existing.first_name === imported.firstName &&
        existing.last_name === imported.lastName &&
        existing.user_group === imported.userGroup;

      if (isIdentical) {
        report.identical++;
      } else {
        toUpsert.push({
          ...existing,
          source_member_id: imported.sourceMemberID || existing.source_member_id,
          source_membership_id: imported.sourceMembershipID || existing.source_membership_id,
          source_email: imported.sourceEmail || existing.source_email,
          first_name: imported.firstName || existing.first_name,
          last_name: imported.lastName || existing.last_name,
          user_group: imported.userGroup || existing.user_group,
          membership_type: imported.membershipType || existing.membership_type,
          membership_status: imported.membershipStatus || existing.membership_status,
          last_reconciled_at: new Date().toISOString()
        });
        report.reconciled++;
      }
    }
  });

  return { toUpsert, report };
};